import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { ShieldCheck, Phone, Mail, FileText, HeartPulse, AlertTriangle, Lock } from "lucide-react";
import Logo from "@/components/Logo";

const Section = ({ icon: Icon, title, emptyText, empty, children }) => (
  <section className="ag-card p-6 md:p-7">
    <div className="flex items-center gap-2 mb-4">
      <div className="h-8 w-8 rounded-lg bg-slate-100 flex items-center justify-center">
        <Icon className="h-4 w-4 text-ag-ink" strokeWidth={1.5} />
      </div>
      <h2 className="font-serif text-2xl text-ag-ink">{title}</h2>
    </div>
    {empty ? <div className="text-sm text-slate-500">{emptyText}</div> : children}
  </section>
);

export default function EmergencyPortalPage() {
  const { token } = useParams();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [errStatus, setErrStatus] = useState(null);

  useEffect(() => {
    api.get(`/emergency/access/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => {
        setErrStatus(e.response?.status);
        setError(formatApiError(e.response?.data?.detail, "Diese Vertrauenskarte ist ungültig."));
      });
  }, [token]);

  if (error) {
    const locked = errStatus === 410;
    return (
      <div className="min-h-screen bg-ag-paper ag-paper-grain flex items-center justify-center p-6">
        <div className="max-w-md w-full ag-card p-8 text-center">
          <div className="h-14 w-14 rounded-2xl bg-red-50 mx-auto flex items-center justify-center mb-4">
            {locked ? <Lock className="h-6 w-6 text-red-600" /> : <AlertTriangle className="h-6 w-6 text-red-600" />}
          </div>
          <h1 className="font-serif text-3xl text-ag-ink mb-2">
            {locked ? t.emergencyPortal.lockedTitle : t.emergencyPortal.notFoundTitle}
          </h1>
          <p className="text-sm text-slate-600">{locked ? t.emergencyPortal.lockedSub : t.emergencyPortal.notFoundSub}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-ag-paper flex items-center justify-center">
        <div className="text-sm text-slate-500">{t.common.loading}</div>
      </div>
    );
  }

  const medicalLabels = t.trustCard.permissions.items;

  return (
    <div className="min-h-screen bg-ag-paper ag-paper-grain">
      <header className="ag-sidebar text-slate-100 px-6 md:px-12 py-8">
        <div className="max-w-3xl mx-auto">
          <Logo variant="full" tone="dark" className="h-16 w-auto -ml-2 mb-3" />
          <h1 className="font-serif text-4xl md:text-5xl leading-tight mt-3">{t.emergencyPortal.title}</h1>
          <p className="text-slate-300 max-w-xl mt-3">{t.emergencyPortal.subtitle}</p>
          <div className="mt-4 inline-flex items-center gap-2 text-xs text-amber-200/90 bg-white/5 border border-white/10 rounded-full pl-3 pr-4 py-1.5" data-testid="portal-trust-hint">
            <ShieldCheck className="h-3.5 w-3.5" strokeWidth={1.6} />
            <span className="leading-snug">{t.trustHints.emergencyPortal}</span>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">{t.emergencyPortal.ownerLabel}</div>
              <div className="font-serif text-xl text-white mt-0.5" data-testid="portal-owner">{data.owner_name}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">{t.emergencyPortal.notfallIdLabel}</div>
              <div className="font-mono text-amber-300 mt-0.5" data-testid="portal-notfall-id">{data.notfall_id}</div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-12 py-10 space-y-6">
        {data.verification_required && (
          <div className="ag-card p-5 border-amber-300 bg-amber-50/60 flex items-start gap-3" data-testid="portal-verification-banner">
            <AlertTriangle className="h-5 w-5 text-amber-700 mt-0.5" />
            <div>
              <div className="font-medium text-ag-ink">{t.emergencyPortal.verifyTitle}</div>
              <div className="text-sm text-slate-600 mt-0.5">{t.emergencyPortal.verifySub}</div>
            </div>
          </div>
        )}

        <Section icon={HeartPulse} title={t.emergencyPortal.sectionMedical} emptyText={t.emergencyPortal.empty} empty={Object.keys(data.medical || {}).length === 0}>
          <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
            {Object.entries(data.medical || {}).map(([key, value]) => (
              <div key={key} className="flex items-start justify-between gap-4 py-1.5 border-b border-slate-50">
                <dt className="text-slate-500">{medicalLabels[key] || key}</dt>
                <dd className="text-ag-ink text-right max-w-[60%]" data-testid={`portal-medical-${key}`}>
                  {key === "organ_donor"
                    ? (value ? t.emergencyPortal.organYes : t.emergencyPortal.organNo)
                    : String(value)}
                </dd>
              </div>
            ))}
          </dl>
        </Section>

        <Section icon={Phone} title={t.emergencyPortal.sectionContacts} emptyText={t.emergencyPortal.empty} empty={!data.contacts?.length}>
          <ul className="space-y-3">
            {data.contacts.map((c, i) => (
              <li key={i} className="rounded-xl border border-slate-100 p-4" data-testid={`portal-contact-${i}`}>
                <div className="font-serif text-lg text-ag-ink">{c.name}</div>
                <div className="text-xs text-slate-500">{c.relationship}</div>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                  {c.phone && (
                    <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1.5 text-ag-ink hover:underline">
                      <Phone className="h-3.5 w-3.5 text-slate-400" /> {c.phone}
                    </a>
                  )}
                  {c.email && (
                    <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1.5 text-ag-ink hover:underline">
                      <Mail className="h-3.5 w-3.5 text-slate-400" /> {c.email}
                    </a>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </Section>

        <Section icon={FileText} title={t.emergencyPortal.sectionDocuments} emptyText={t.emergencyPortal.empty} empty={!data.documents?.length}>
          <ul className="space-y-2">
            {(data.documents || []).map((d) => (
              <li key={d.id} className="flex items-start gap-3 rounded-xl border border-slate-100 p-4">
                <FileText className="h-4 w-4 text-slate-400 mt-0.5" />
                <div>
                  <div className="font-medium text-ag-ink">{d.title}</div>
                  {d.description && <div className="text-xs text-slate-500 mt-0.5">{d.description}</div>}
                </div>
              </li>
            ))}
          </ul>
        </Section>

        <Section icon={ShieldCheck} title={t.emergencyPortal.sectionNotes} emptyText={t.emergencyPortal.empty} empty={Object.keys(data.notes || {}).length === 0}>
          <dl className="space-y-3 text-sm">
            {Object.entries(data.notes || {}).map(([key, value]) => (
              <div key={key} className="rounded-xl border border-slate-100 p-4">
                <dt className="text-xs uppercase tracking-wider text-slate-500 mb-1">{medicalLabels[key] || key}</dt>
                <dd className="text-ag-ink" data-testid={`portal-note-${key}`}>{String(value)}</dd>
              </div>
            ))}
          </dl>
        </Section>

        <p className="text-xs text-slate-500 text-center pt-4">{t.emergencyPortal.footer}</p>
      </main>
    </div>
  );
}
